/**
 * Done. / ChiroCPD — OnboardingSurvey Feature Component
 *
 * Multi-step survey: 5 steps → AI plan generation → redirect to /dashboard.
 * Focus moves to first interactive element on each step transition.
 * No aria-live on step transition (prevents double-speak with focus movement).
 * All AI output labelled "AI-generated draft".
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AILabel } from '@/components/ui/AILabel';
import { api, ApiError } from '@/lib/api';
import type { SurveyData, PracticeType, AnnualPlan } from '@/lib/types';

interface OnboardingSurveyProps {
  onComplete?: () => void;
}

type Step = 1 | 2 | 3 | 4 | 5 | 6; // 6 = generating plan

const PRACTICE_TYPE_OPTIONS: { value: PracticeType; label: string }[] = [
  { value: 'solo',           label: 'Solo practice'         },
  { value: 'small_practice', label: 'Small practice (2–5)'  },
  { value: 'large_practice', label: 'Large practice (6+)'   },
  { value: 'locum',          label: 'Locum'                  },
  { value: 'academic',       label: 'Academic / research'   },
  { value: 'other',          label: 'Other'                  },
];

export function OnboardingSurvey({ onComplete }: OnboardingSurveyProps): React.ReactElement {
  const [, navigate] = useLocation();
  const [step, setStep] = useState<Step>(1);
  const [surveyData, setSurveyData] = useState<Partial<SurveyData>>({});
  const [generating, setGenerating] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState<AnnualPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Move focus to first element on step change
  useEffect(() => {
    firstInputRef.current?.focus();
  }, [step]);

  function update(patch: Partial<SurveyData>): void {
    setSurveyData(prev => ({ ...prev, ...patch }));
  }

  function nextStep(): void {
    setStep(s => (s < 5 ? (s + 1) as Step : s));
  }

  function prevStep(): void {
    setStep(s => (s > 1 ? (s - 1) as Step : s));
  }

  const handleGenerate = useCallback(async (): Promise<void> => {
    setStep(6);
    setGenerating(true);
    setError(null);
    try {
      const plan = await api.post<AnnualPlan>('/api/ai/generate-plan', { surveyData });
      setGeneratedPlan(plan);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('AI generation limit reached. Your plan has been saved and you can edit it manually.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to generate plan. You can set up your plan manually.');
      }
    } finally {
      setGenerating(false);
    }
  }, [surveyData]);

  const handleFinish = useCallback((): void => {
    onComplete?.();
    navigate('/dashboard');
  }, [onComplete, navigate]);

  return (
    <div className="flex flex-col gap-6">
      {/* Step 1: Name */}
      {step === 1 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            Welcome to ChiroCPD
          </h2>
          <p className="text-sm text-[var(--text-secondary)]">
            Let&apos;s set up your CPD plan. This takes about 2 minutes.
          </p>
          <Input
            id="name"
            label="Your full name"
            value={surveyData.name ?? ''}
            onChange={v => { update({ name: v }); }}
            required
            autoComplete="name"
            placeholder="e.g. Jo Miles"
          />
          <div className="flex justify-end">
            <Button
              variant="primary"
              onClick={nextStep}
              ariaDisabled={!surveyData.name?.trim()}
            >
              Next →
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Practice type */}
      {step === 2 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            Your Practice
          </h2>
          <p className="text-sm text-[var(--text-secondary)]">
            How long have you been registered, and what type of practice do you work in?
          </p>

          <Input
            id="yearsRegistered"
            label="Years registered as a chiropractor"
            type="number"
            value={String(surveyData.yearsRegistered ?? '')}
            onChange={v => { update({ yearsRegistered: parseInt(v, 10) || 0 }); }}
            required
            min={0}
            max={60}
            placeholder="e.g. 8"
          />

          <div className="flex flex-col gap-1">
            <label htmlFor="practiceType" className="text-sm font-medium text-[var(--text-primary)]">
              Practice type
              <span className="text-[var(--color-error-500)] ml-1" aria-hidden="true">*</span>
            </label>
            <select
              id="practiceType"
              value={surveyData.practiceType ?? ''}
              onChange={e => { update({ practiceType: e.target.value as PracticeType }); }}
              className="w-full rounded-[var(--radius-md)] border border-[var(--border-base)] px-3 py-2 text-sm min-h-[44px] bg-[var(--surface-base)] focus:outline-none focus:ring-2 focus:ring-[var(--color-done-500)]"
            >
              <option value="">Select…</option>
              {PRACTICE_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={prevStep}>← Back</Button>
            <Button
              variant="primary"
              onClick={nextStep}
              ariaDisabled={!surveyData.practiceType}
            >
              Next →
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Interests */}
      {step === 3 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            Your CPD Interests
          </h2>

          <div className="flex flex-col gap-1">
            <label htmlFor="specialInterests" className="text-sm font-medium text-[var(--text-primary)]">
              Special clinical interests
              <span className="ml-1 text-xs font-normal text-[var(--text-muted)]">(optional)</span>
            </label>
            <textarea
              id="specialInterests"
              value={surveyData.specialInterests ?? ''}
              onChange={e => { update({ specialInterests: e.target.value }); }}
              rows={3}
              placeholder="e.g. sports chiropractic, paediatrics, rehabilitation…"
              className="w-full rounded-[var(--radius-md)] border border-[var(--border-base)] px-3 py-2 text-sm bg-[var(--surface-base)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-done-500)] resize-y"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="weakAreas" className="text-sm font-medium text-[var(--text-primary)]">
              Areas for development
              <span className="ml-1 text-xs font-normal text-[var(--text-muted)]">(optional)</span>
            </label>
            <textarea
              id="weakAreas"
              value={surveyData.weakAreas ?? ''}
              onChange={e => { update({ weakAreas: e.target.value }); }}
              rows={3}
              placeholder="e.g. evidence-based practice, communication skills…"
              className="w-full rounded-[var(--radius-md)] border border-[var(--border-base)] px-3 py-2 text-sm bg-[var(--surface-base)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-done-500)] resize-y"
            />
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={prevStep}>← Back</Button>
            <Button variant="primary" onClick={nextStep}>Next →</Button>
          </div>
        </div>
      )}

      {/* Step 4: Goals */}
      {step === 4 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            Your Goals
          </h2>

          <div className="flex flex-col gap-1">
            <label htmlFor="goalsNextYear" className="text-sm font-medium text-[var(--text-primary)]">
              What do you want to achieve in the next CPD year?
              <span className="ml-1 text-xs font-normal text-[var(--text-muted)]">(optional)</span>
            </label>
            <textarea
              id="goalsNextYear"
              value={surveyData.goalsNextYear ?? ''}
              onChange={e => { update({ goalsNextYear: e.target.value }); }}
              rows={4}
              placeholder="e.g. improve diagnostic accuracy, complete a postgrad paper…"
              className="w-full rounded-[var(--radius-md)] border border-[var(--border-base)] px-3 py-2 text-sm bg-[var(--surface-base)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-done-500)] resize-y"
            />
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={prevStep}>← Back</Button>
            <Button variant="primary" onClick={nextStep}>Next →</Button>
          </div>
        </div>
      )}

      {/* Step 5: Cultural safety */}
      {step === 5 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            <span lang="mi">Hauora</span> &amp; Cultural Safety
          </h2>
          <p className="text-sm text-[var(--text-secondary)]">
            The Board&apos;s 2024 competency standards include Cultural Safety as a core principle.
          </p>

          <div className="flex flex-col gap-1">
            <label htmlFor="culturalSafetyFocus" className="text-sm font-medium text-[var(--text-primary)]">
              Cultural safety focus area
              <span className="ml-1 text-xs font-normal text-[var(--text-muted)]">(optional)</span>
            </label>
            <textarea
              id="culturalSafetyFocus"
              value={surveyData.culturalSafetyFocus ?? ''}
              onChange={e => { update({ culturalSafetyFocus: e.target.value }); }}
              rows={3}
              placeholder={`e.g. Te Tiriti o Waitangi in practice, working with tangata whenua…`}
              className="w-full rounded-[var(--radius-md)] border border-[var(--border-base)] px-3 py-2 text-sm bg-[var(--surface-base)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-done-500)] resize-y"
            />
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={prevStep}>← Back</Button>
            <Button
              variant="primary"
              onClick={() => { void handleGenerate(); }}
            >
              Generate My Plan ◆
            </Button>
          </div>
        </div>
      )}

      {/* Step 6: Generating */}
      {step === 6 && (
        <div className="flex flex-col items-center gap-6 py-8 text-center">
          {generating ? (
            <>
              <svg className="h-10 w-10 animate-spin text-[var(--color-done-500)]" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <div>
                <p className="text-base font-semibold text-[var(--text-primary)]">Generating your personalised CPD plan…</p>
                <p className="text-sm text-[var(--text-secondary)] mt-1">This usually takes 10–15 seconds.</p>
              </div>
            </>
          ) : error ? (
            <>
              <p className="text-sm text-[var(--text-secondary)]">{error}</p>
              <Button variant="primary" onClick={handleFinish}>
                Continue to Dashboard →
              </Button>
            </>
          ) : generatedPlan ? (
            <>
              <div className="w-full text-left">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-semibold text-[var(--text-primary)]">Your Annual CPD Plan</h3>
                  <AILabel />
                </div>
                <div className="rounded-[var(--radius-md)] bg-[var(--surface-subtle)] border border-[var(--border-base)] px-4 py-3 text-sm text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                  {generatedPlan.planText}
                </div>
                <p className="mt-2 text-xs text-[var(--text-muted)]">
                  Review and edit this plan at any time from your Dashboard.
                </p>
              </div>
              <Button variant="primary" onClick={handleFinish}>
                Go to Dashboard →
              </Button>
            </>
          ) : null}
        </div>
      )}

      {/* Hidden focus target for step transitions */}
      <input ref={firstInputRef} aria-hidden="true" tabIndex={-1} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} />
    </div>
  );
}
